import { Camera, Wand2, Store } from "lucide-react";
import { useTranslation } from "@/lib/i18n";

export function HowItWorks() {
  const { t } = useTranslation();

  const steps = [
    {
      icon: Camera,
      number: "01",
      titleKey: "howItWorks.snapYourReceipt",
      descriptionKey: "howItWorks.snapYourReceiptDesc",
    },
    {
      icon: Wand2,
      number: "02",
      titleKey: "howItWorks.refoundEnrichesIt",
      descriptionKey: "howItWorks.refoundEnrichesItDesc",
    },
    {
      icon: Store,
      number: "03",
      titleKey: "howItWorks.useItWhenNeeded",
      descriptionKey: "howItWorks.useItWhenNeededDesc",
    },
  ];

  return (
    <section id="how-it-works" className="py-20 sm:py-28 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div className="max-w-3xl mx-auto text-center mb-16 animate-fade-up">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            <span className="font-heading text-primary">{t("howItWorks.howItWorksTitle")}</span>
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            {t("howItWorks.howItWorksSubtitle")}
          </p>
        </div>

        {/* Desktop layout: horizontal cards with dashed connector */}
        <div className="hidden lg:block max-w-6xl mx-auto">
          <div className="relative grid grid-cols-3 gap-8">
            {/* Dashed connector lines between cards */}
            <div
              className="absolute top-[72px] left-[calc(33.33%+16px)] right-[calc(66.67%-16px)] z-0"
              aria-hidden="true"
            >
              <div className="h-[2px] w-full border-t-2 border-dashed border-primary/30 relative overflow-hidden">
                <div className="absolute inset-0 h-full w-1/2 bg-gradient-to-r from-primary/30 to-primary animate-[shimmer-sweep_4s_ease-in-out_infinite]" />
              </div>
            </div>
            <div
              className="absolute top-[72px] left-[calc(66.67%+16px)] right-[calc(0%+48px)] z-0"
              aria-hidden="true"
            >
              <div className="h-[2px] w-full border-t-2 border-dashed border-primary/30 relative overflow-hidden">
                <div className="absolute inset-0 h-full w-1/2 bg-gradient-to-r from-primary/30 to-primary animate-[shimmer-sweep_4s_ease-in-out_infinite]" style={{ animationDelay: "1s" }} />
              </div>
            </div>

            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
              <div
                key={step.titleKey}
                className={`animate-fade-up stagger-${index + 2}`}
              >
                <div className="glass-card hover-lift rounded-2xl p-8 relative overflow-hidden group transition-shadow duration-300 hover:shadow-[0_8px_40px_-8px_hsl(160_72%_42%/0.2)]">
                  {/* Large watermark number */}
                  <span
                    className="absolute top-4 right-6 font-heading text-[80px] leading-none font-bold text-primary/20 select-none pointer-events-none"
                    aria-hidden="true"
                  >
                    {step.number}
                  </span>

                  {/* Icon with pulse-ring on hover */}
                  <div className="relative inline-flex mb-6 z-10">
                    <div className="relative w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center group-hover:shadow-lg transition-shadow duration-300">
                      <Icon className="w-6 h-6" />
                    </div>
                    {/* Pulse ring - visible on hover */}
                    <div className="absolute inset-0 rounded-full bg-primary/40 opacity-0 group-hover:animate-[pulse-ring_1.2s_ease-out_infinite] pointer-events-none" />
                  </div>

                  {/* Card content */}
                  <h3 className="font-sans font-semibold text-lg text-foreground mb-2 relative z-10">
                    {t(step.titleKey)}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed relative z-10">
                    {t(step.descriptionKey)}
                  </p>
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Mobile / Tablet layout: vertical timeline */}
        <div className="lg:hidden max-w-md mx-auto">
          <div className="relative">
            {/* Vertical connecting line on the left */}
            <div
              className="absolute left-7 top-0 bottom-0 w-[2px] bg-gradient-to-b from-primary/30 via-primary to-primary/30"
              aria-hidden="true"
            />

            <div className="flex flex-col gap-8">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                <div
                  key={step.titleKey}
                  className={`relative pl-20 animate-fade-up stagger-${index + 2}`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-6 z-10">
                    <div className="relative w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg">
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>

                  {/* Card */}
                  <div className="glass-card hover-lift rounded-2xl p-6 relative overflow-hidden group transition-shadow duration-300 hover:shadow-[0_8px_40px_-8px_hsl(160_72%_42%/0.2)]">
                    {/* Large watermark number */}
                    <span
                      className="absolute top-2 right-4 font-heading text-[64px] leading-none font-bold text-primary/20 select-none pointer-events-none"
                      aria-hidden="true"
                    >
                      {step.number}
                    </span>

                    <h3 className="font-sans font-semibold text-lg text-foreground mb-2 relative z-10">
                      {t(step.titleKey)}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed relative z-10">
                      {t(step.descriptionKey)}
                    </p>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
